import React from 'react';
import { Box, Unstable_Grid2 as Grid } from '@mui/material';

import { Container, Select, StackedBarChart } from 'components';
import { useTranslations } from 'hooks/useTranslations';

import { CustomColor, customColors } from 'config/customColors';
import { CustomString } from 'config/translations';
import { UseQuery } from 'useQuery';
import { BusinessSpendCategory } from 'useQuery/types';

type CategoryId =
  | 'check'
  | 'mywheels'
  | 'mywheelsCommission'
  | 'greenwheels'
  | 'greenwheelsCommission'
  | 'greenwheels_rnp'
  | 'greenwheels_rnpCommission'
  | 'adyen'
  | 'ns'
  | 'mrr'
  | 'usageFee'
  | 'oneTimeFee'
  | 'credit'
  | 'passthrough'
  | 'other';

type FilterId = 'all' | 'revenue' | 'mrr' | 'gmv';

type VatId = 'excl' | 'incl';

// The spend tags each category sums up; a category can carry more than one tag.
const CATEGORIES: { id: CategoryId; tags: BusinessSpendCategory[]; color: CustomColor }[] = [
  { id: 'check', tags: ['CHECK'], color: customColors.check },
  { id: 'mywheels', tags: ['MYWHEELS'], color: customColors.mywheels },
  {
    id: 'mywheelsCommission',
    tags: ['MYWHEELS Commission'],
    color: customColors.mywheels_commission,
  },
  { id: 'greenwheels', tags: ['GREENWHEELS'], color: customColors.greenwheels },
  {
    id: 'greenwheelsCommission',
    tags: ['GREENWHEELS Commission'],
    color: customColors.greenwheels_commission,
  },
  { id: 'greenwheels_rnp', tags: ['GREENWHEELS_RNP'], color: customColors.greenwheels_rnp },
  {
    id: 'greenwheels_rnpCommission',
    tags: ['GREENWHEELS_RNP Commission'],
    color: customColors.greenwheels_rnp_commission,
  },
  { id: 'adyen', tags: ['ADYEN'], color: customColors.adyen },
  { id: 'ns', tags: ['NS'], color: customColors.ns },
  // REVENUE is the legacy tag; older months still carry it.
  { id: 'mrr', tags: ['SUBSCRIPTION', 'REVENUE'], color: customColors.mrr },
  { id: 'usageFee', tags: ['USAGE_FEE'], color: customColors.usage_fee },
  { id: 'oneTimeFee', tags: ['ONETIMEFEE'], color: customColors.one_time_fee },
  { id: 'credit', tags: ['CREDIT'], color: customColors.credit },
  { id: 'passthrough', tags: ['PASSTHROUGH'], color: customColors.passthrough },
  { id: 'other', tags: ['OTHER'], color: customColors.other },
];

const FILTERS: Record<FilterId, CategoryId[] | undefined> = {
  all: undefined,
  revenue: [
    'mrr',
    'mywheelsCommission',
    'greenwheelsCommission',
    'greenwheels_rnpCommission',
    'usageFee',
    'oneTimeFee',
  ],
  mrr: ['mrr'],
  gmv: ['mywheels', 'greenwheels', 'greenwheels_rnp', 'adyen', 'ns', 'check', 'passthrough'],
};

export function BusinessStatistics() {
  const { data: businessStatisticsData } = UseQuery.businessStatistics();
  const translations = useTranslations('statisticsPage');

  const [filter, setFilter] = React.useState<FilterId>('all');
  const [vat, setVat] = React.useState<VatId>('incl');

  const filterOptions: { id: FilterId; label: CustomString }[] = [
    { id: 'all', label: translations.all },
    { id: 'revenue', label: translations.filtersRevenueOnly },
    { id: 'mrr', label: translations.filtersMrrOnly },
    { id: 'gmv', label: translations.filtersGmv },
  ];

  const vatOptions: { id: VatId; label: CustomString }[] = [
    { id: 'excl', label: translations.vatExcl },
    { id: 'incl', label: translations.vatIncl },
  ];

  // Both variants are in the same response, so switching does not refetch.
  const spend = vat === 'incl' ? businessStatisticsData?.spend : businessStatisticsData?.spend_net;

  const enabledCategoryIds = FILTERS[filter];

  function isEnabled(id: CategoryId) {
    return enabledCategoryIds === undefined || enabledCategoryIds.includes(id);
  }

  const byMonth = spend?.by_month ?? [];

  return (
    <Grid container spacing={2} sx={{ paddingTop: 1 }}>
      <Container
        title={translations.spendByProvider}
        width="flex"
        loading={businessStatisticsData === undefined}
        headerAction={
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Box sx={{ width: 220 }}>
              <Select
                mode="single"
                label={translations.filters}
                value={filter}
                options={filterOptions}
                onChange={setFilter}
                fullWidth
              />
            </Box>
            <Box sx={{ width: 160 }}>
              <Select
                mode="single"
                label={translations.vat}
                value={vat}
                options={vatOptions}
                onChange={setVat}
                fullWidth
              />
            </Box>
          </Box>
        }
      >
        <StackedBarChart
          // Remount on filter change so the disabled series are not overruled by
          // earlier legend clicks, which Chart.js remembers per dataset index.
          key={`${filter}-${vat}`}
          labels={byMonth.map((item) => Object.keys(item)[0])}
          data={CATEGORIES.map(({ id, tags }) => ({
            label: translations[id],
            hidden: !isEnabled(id),
            values: byMonth.map((item) => {
              const key = Object.keys(item)[0];

              return item[key]
                ? tags.reduce((total, tag) => total + (item[key][tag] ?? 0), 0) / 100
                : 0;
            }),
          }))}
          colors={CATEGORIES.map(({ color }) => color)}
        />
      </Container>
    </Grid>
  );
}
